/**
 * Admin service for dashboard, tickets, employees, and users.
 * Cookie-based session auth with centralized fetch helpers.
 */
import { API_BASE_URL } from '../config/env';
import {
  DASHBOARD_ENDPOINTS,
  ADMIN_TICKET_ENDPOINTS,
  EMPLOYEE_ENDPOINTS,
  ADMIN_USER_ENDPOINTS,
  ADMIN_AUTH_ENDPOINTS,
} from '../constants/apiConstants';

// ─── Fetch Helpers ───────

/**
 * Centralized fetch helper for admin-side API calls.
 * @param {string} endpoint - API endpoint path (e.g., '/admin/tickets')
 * @param {RequestInit} [options] - Additional fetch options
 * @returns {Promise<Response>}
 */
const adminFetch = (endpoint, options = {}) => {
  return fetch(`${API_BASE_URL}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

/** GET convenience wrapper. */
const adminGet = (endpoint) => adminFetch(endpoint);

/** POST convenience wrapper. */
const adminPost = (endpoint, body) =>
  adminFetch(endpoint, { method: 'POST', body: JSON.stringify(body) });

/** PUT convenience wrapper. */
const adminPut = (endpoint, body) =>
  adminFetch(endpoint, { method: 'PUT', body: JSON.stringify(body) });

/** DELETE convenience wrapper. */
const adminDelete = (endpoint) =>
  adminFetch(endpoint, { method: 'DELETE' });

// ─── Auth ────────────────

/**
 * Admin login.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Response>}
 */
export const adminLogin = (email, password) => {
  return adminPost(ADMIN_AUTH_ENDPOINTS.LOGIN, { email, password });
};

/**
 * Admin logout.
 * @returns {Promise<Response>}
 */
export const adminLogout = () => {
  return adminPost(ADMIN_AUTH_ENDPOINTS.LOGOUT);
};

// ─── Dashboard ───────────

/**
 * Fetch dashboard stats (ticket counts by status/urgency).
 * @returns {Promise<Response>}
 */
export const fetchDashboardStats = () => {
  return adminGet(DASHBOARD_ENDPOINTS.STATS);
};

/**
 * Fetch ticket urgency/status breakdown.
 * @returns {Promise<Response>}
 */
export const fetchTicketsBreakdown = () => {
  return adminGet(DASHBOARD_ENDPOINTS.TICKETS_BREAKDOWN);
};

// ─── Tickets ─────────────

/**
 * Fetch all admin tickets.
 * @returns {Promise<Response>}
 */
export const fetchAdminTickets = () => {
  return adminGet(ADMIN_TICKET_ENDPOINTS.LIST);
};

/**
 * Fetch unassigned tickets.
 * @returns {Promise<Response>}
 */
export const fetchUnassignedTickets = () => {
  return adminGet(ADMIN_TICKET_ENDPOINTS.UNASSIGNED);
};

/**
 * Fetch a single ticket by ID.
 * @param {string} id
 * @returns {Promise<Response>}
 */
export const fetchTicketDetail = (id) => {
  return adminGet(ADMIN_TICKET_ENDPOINTS.DETAIL(id));
};

/**
 * Update ticket status.
 * @param {string} id
 * @param {string} status
 * @returns {Promise<Response>}
 */
export const updateTicketStatus = (id, status) => {
  return adminPut(ADMIN_TICKET_ENDPOINTS.UPDATE_STATUS(id), { status });
};

/**
 * Retry the AI draft -> review -> push-to-Jira pipeline for a ticket.
 * Only succeeds server-side when the ticket isn't already synced.
 * @param {string} id
 * @returns {Promise<Response>}
 */
export const retryJiraSync = (id) => {
  return adminPost(ADMIN_TICKET_ENDPOINTS.JIRA_SYNC(id));
};

/**
 * Delete a ticket (also deletes its linked Jira issue, if any).
 * @param {string} id
 * @returns {Promise<Response>}
 */
export const deleteTicket = (id) => {
  return adminDelete(ADMIN_TICKET_ENDPOINTS.DELETE(id));
};

/**
 * Reply to a ticket.
 * @param {string} id
 * @param {string} reply
 * @returns {Promise<Response>}
 */
export const replyToTicket = (id, reply) => {
  return adminPost(ADMIN_TICKET_ENDPOINTS.REPLY(id), { reply });
};

/**
 * Assign a ticket to an employee.
 * @param {string} ticketId
 * @param {string} employeeId
 * @returns {Promise<Response>}
 */
export const assignTicket = (ticketId, employeeId) => {
  return adminPost(ADMIN_TICKET_ENDPOINTS.ASSIGN, {
    ticket_id: ticketId,
    employee_id: employeeId,
  });
};

/**
 * Fetch all comments for a ticket (includes internal notes).
 * @param {string} id
 * @returns {Promise<Response>}
 */
export const fetchTicketComments = (id) => {
  return adminGet(ADMIN_TICKET_ENDPOINTS.COMMENTS(id));
};

/**
 * Add a comment to a ticket as admin/employee.
 * @param {string} id
 * @param {string} content
 * @param {boolean} isInternal
 * @returns {Promise<Response>}
 */
export const addTicketComment = (id, content, isInternal = false) => {
  return adminPost(ADMIN_TICKET_ENDPOINTS.COMMENTS(id), {
    content,
    is_internal: isInternal,
  });
};

// ─── Employees ───────────

/**
 * Fetch all employees.
 * @returns {Promise<Response>}
 */
export const fetchEmployees = () => {
  return adminGet(EMPLOYEE_ENDPOINTS.LIST);
};

/**
 * Add a new employee.
 * @param {object} data - { name, email, password }
 * @returns {Promise<Response>}
 */
export const createEmployee = (data) => {
  return adminPost(EMPLOYEE_ENDPOINTS.CREATE, data);
};

/**
 * Update employee (e.g., toggle active status).
 * @param {string} id
 * @param {object} data - { is_active }
 * @returns {Promise<Response>}
 */
export const updateEmployee = (id, data) => {
  return adminPut(EMPLOYEE_ENDPOINTS.UPDATE(id), data);
};

/**
 * Delete an employee.
 * @param {string} id
 * @returns {Promise<Response>}
 */
export const deleteEmployee = (id) => {
  return adminDelete(EMPLOYEE_ENDPOINTS.DELETE(id));
};

// ─── Users ───────────────

/**
 * Fetch all registered users.
 * @returns {Promise<Response>}
 */
export const fetchUsers = () => {
  return adminGet(ADMIN_USER_ENDPOINTS.LIST);
};